import { EXTRACT_OCR_STATUS } from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { ValueOf } from "core/types";
import { Banner } from "ds/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";

export const HEADER_BANNERS = {
  EXTRACT_COMPLETED: "extract-completed",
  EXTRACT_COMPLETED_NONE_FILLED: "extract-completed-none-filled",
  EXTRACT_FAILED: "extract-failed",
  EXTRACTING: "extracting",
  FORM_DIRTY_WARNING: "form-dirty-warning",
  IMPORT_WARNING: "import-warning",
  NO_FILES_WARNING: "no-files-warning",
} as const;

export type HeaderBannerType = ValueOf<typeof HEADER_BANNERS>;

export type HeaderBannerAction =
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_COMPLETED;
      context: { numFilledFields: number };
    }
  | {
      banner: typeof HEADER_BANNERS.IMPORT_WARNING;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACTING;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_FAILED;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED;
    }
  | {
      banner: typeof HEADER_BANNERS.NO_FILES_WARNING;
    }
  | {
      banner: typeof HEADER_BANNERS.FORM_DIRTY_WARNING;
    }
  | {
      banner: null;
    };

const EXTRACTION_BANNER_INTERVAL = 15_000;

const ExtractionBanner = ({
  headerBannerAction,
}: {
  headerBannerAction: HeaderBannerAction;
}) => {
  const translations = useTranslations();
  const { extractOcrStatus } = useExtractDocs();

  const [fileIndex, setFileIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const documents = extractOcrStatus.result?.documents ?? [];
  const extractedFiles = useMemo(
    () =>
      documents.filter((doc) => doc.status === EXTRACT_OCR_STATUS.EXTRACTED),
    [documents],
  );

  const lenExtractedFiles = extractedFiles.length;
  const isFinal = fileIndex >= lenExtractedFiles;

  useEffect(() => {
    setFileIndex((i) => Math.min(i, lenExtractedFiles));
  }, [lenExtractedFiles]);

  useEffect(() => {
    const isExtracting =
      headerBannerAction.banner === HEADER_BANNERS.EXTRACTING;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isExtracting || lenExtractedFiles === 0) return;

    if (fileIndex >= lenExtractedFiles) return;

    intervalRef.current = setInterval(() => {
      setFileIndex((prev) => Math.min(prev + 1, lenExtractedFiles));
    }, EXTRACTION_BANNER_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [headerBannerAction.banner, lenExtractedFiles, fileIndex]);

  if (lenExtractedFiles === 0) return null;

  return (
    <Banner
      color="primary"
      heading={
        isFinal
          ? translations.extractDocs.splitView.header.bannerReadingDocsFinal
          : translations.extractDocs.splitView.header.bannerReadingFile({
              fileName: extractedFiles[fileIndex]?.filename ?? "",
            })
      }
      isLoading
      message={
        isFinal
          ? translations.extractDocs.splitView.header
              .bannerReadingDocsFinalDescription
          : translations.extractDocs.splitView.header
              .bannerReadingFileDescription
      }
    />
  );
};

export const HeaderBanners = ({
  headerBannerAction,
  importData,
  onClose,
}: {
  headerBannerAction: HeaderBannerAction;
  importData: () => void;
  onClose: () => void;
}) => {
  const translations = useTranslations();
  const { setFormModal } = useRecareDocsContext();

  switch (headerBannerAction.banner) {
    case HEADER_BANNERS.IMPORT_WARNING:
      return (
        <Banner
          actions={[
            {
              id: "refresh",
              label:
                translations.extractDocs.docsCreation.refreshWarningModal
                  .buttonRefresh,
              onPress: importData,
            },
          ]}
          color="warning"
          heading={
            translations.extractDocs.docsCreation.refreshWarningModal.title
          }
          message={
            translations.extractDocs.docsCreation.refreshWarningModal
              .description
          }
          withClose
          onClose={onClose}
        />
      );
    case HEADER_BANNERS.EXTRACT_COMPLETED:
      return (
        <Banner
          color="success"
          heading={translations.extractDocs.docsCreation.importModalSummary.title(
            {
              count: headerBannerAction.context.numFilledFields.toString(),
            },
          )}
          message={
            <div className="flex flex-col gap-4">
              <p>
                {
                  translations.extractDocs.docsCreation.importModalSummary
                    .filledWithExtractedData
                }
              </p>
              <p className="font-bold">
                {
                  translations.extractDocs.docsCreation.importModalSummary
                    .disclaimerExtractedData
                }
              </p>
            </div>
          }
          withClose
          onClose={onClose}
        />
      );
    case HEADER_BANNERS.EXTRACTING:
      return <ExtractionBanner headerBannerAction={headerBannerAction} />;
    case HEADER_BANNERS.EXTRACT_FAILED:
      return (
        <Banner
          color="danger"
          heading={
            translations.extractDocs.docsCreation.bannerDocsInferenceFailedTitle
          }
          message={
            translations.extractDocs.docsCreation
              .bannerInferenceFailedDescription
          }
          withClose
          onClose={onClose}
        />
      );
    case HEADER_BANNERS.NO_FILES_WARNING:
      return (
        <Banner
          color="warning"
          heading={
            translations.extractDocs.docsCreation.bannerNoDocumentsAvailable
              .title
          }
          message={
            translations.extractDocs.docsCreation.bannerNoDocumentsAvailable
              .description
          }
          withClose
          onClose={onClose}
        />
      );
    case HEADER_BANNERS.FORM_DIRTY_WARNING:
      return (
        <Banner
          actions={[
            {
              id: "close",
              label:
                translations.extractDocs.splitView.header.buttonConfirmClose,
              onPress: () => setFormModal(null),
            },
            {
              id: "cancel",
              label: translations.extractDocs.splitView.header.buttonCancel,
              onPress: onClose,
            },
          ]}
          color="warning"
          heading={
            translations.extractDocs.splitView.header.bannerCloseWarningTitle
          }
          message={
            translations.extractDocs.splitView.header
              .bannerCloseWarningDescription
          }
        />
      );
    case HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED:
      return (
        <Banner
          color="warning"
          heading={
            translations.extractDocs.docsCreation.bannerNoExtractedDataImported
              .title
          }
          message={
            translations.extractDocs.docsCreation.bannerNoExtractedDataImported
              .description
          }
          withClose
          onClose={onClose}
        />
      );
    default:
      return null;
  }
};
